// Customer Intelligence - Pre-built rule-based alerts and automated actions
import { useState } from 'react';
import {
  Brain,
  Eye,
  AlertCircle,
  Mail,
  TrendingUp,
  Filter,
  CheckCircle,
  Clock,
  Play,
  Pause,
  Info
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Pre-built intelligence rules catalog
const availableRules = [
  {
    id: '1',
    name: 'Intro Class Completers - No Follow Up',
    description: 'Identifies students who completed "Intro to Sewing" but haven\'t enrolled in another class within 7 days',
    category: 'Student Retention',
    condition: {
      type: 'class_completion',
      classTitle: 'Intro to Sewing',
      noFollowUpEnrollment: true,
      daysSinceCompletion: 7
    },
    action: {
      type: 'email',
      templateName: 'Follow-up Class Recommendations',
      autoSend: false
    },
    matchCount: 0,
    lastRun: null,
    isActive: false
  }
];

const CustomerIntelligence = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState(availableRules);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Get unique categories
  const categories = ['all', ...new Set(rules.map(rule => rule.category))];

  // Filter rules by category
  const filteredRules = selectedCategory === 'all'
    ? rules
    : rules.filter(rule => rule.category === selectedCategory);

  const handleToggleActive = (ruleId) => {
    setRules(rules.map(rule =>
      rule.id === ruleId ? { ...rule, isActive: !rule.isActive } : rule
    ));
  };

  const getStatusBadge = (rule) => {
    if (!rule.isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
          <Pause className="w-3 h-3" />
          Inactive
        </span>
      );
    }

    if (rule.matchCount > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
          <AlertCircle className="w-3 h-3" />
          {rule.matchCount} Match{rule.matchCount !== 1 ? 'es' : ''}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
        <CheckCircle className="w-3 h-3" />
        Active
      </span>
    );
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  // Count active rules
  const activeRulesCount = rules.filter(r => r.isActive).length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Brain className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-white">Customer Intelligence</h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {activeRulesCount} of {filteredRules.length} rule{filteredRules.length !== 1 ? 's' : ''} active • Pre-built insights and actions
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                Pre-built Intelligence Rules
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                These are carefully crafted rules designed to help you identify opportunities and engage with your students effectively.
                Simply activate the rules you want to use, and the system will automatically monitor your data and alert you when conditions are met.
              </p>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category:</label>
            <div className="flex gap-2 flex-wrap">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-[#7A99D8] text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {category === 'all' ? 'All Categories' : category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Rules List */}
        {filteredRules.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <Brain className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-[#3C3C3C] dark:text-white mb-3">
              No rules in this category
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto">
              Try selecting a different category to see available intelligence rules.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRules.map(rule => (
              <div
                key={rule.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-white">
                        {rule.name}
                      </h3>
                      {getStatusBadge(rule)}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {rule.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        {rule.category}
                      </span>
                      {rule.lastRun && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Last run: {new Date(rule.lastRun).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => navigate(`/intelligence/view/${rule.id}`)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(rule.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        rule.isActive
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                          : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                      }`}
                    >
                      {rule.isActive ? (
                        <>
                          <Pause className="w-4 h-4" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4" />
                          Activate
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Action Details */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-2 mb-2">
                    {getActionIcon(rule.action.type)}
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Action: {rule.action.type === 'email' ? 'Send Email' : 'Unknown Action'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Template: {rule.action.templateName}
                    {!rule.action.autoSend && (
                      <span className="ml-2 text-amber-600 dark:text-amber-400">(Manual approval required)</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerIntelligence;
